import { useState } from 'react';
import api from '../api/axios';
import '../styles/style.css';

const emptyForm = {
  shortName: '',
  fullName: '',
  accentColor: '#534AB7',
  contactPhone: '',
  contactEmail: '',
  website: '',
  addressLine: '',
  signatoryName: '',
  signatoryTitle: 'HR Manager',
  einNumber: '',
  everifyNumber: '',
  smtpUser: '',
  smtpAppPassword: '',
  letterheadMode: 'LOGO',
  pageSize: 'LETTER',
  contentTopMarginIn: 1,
  contentBottomMarginIn: 1,
};

// Basic reusable validators
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isValidPhone = (v) => /^[+()\d\s-]{7,20}$/.test(v);

export default function CompanyFormModal({ existing, onClose, onSaved }) {
  // The API never sends the stored SMTP app password (only hasSmtpPassword),
  // so on edit the password field starts empty. Leaving it empty keeps the
  // current password; typing a new one replaces it.
  const [form, setForm] = useState(() =>
    existing ? { ...emptyForm, ...existing, smtpAppPassword: '' } : emptyForm
  );
  const [letterheadFile, setLetterheadFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    // clear that field's error as soon as the user edits it
    if (fieldErrors[k]) setFieldErrors((fe) => ({ ...fe, [k]: undefined }));
  };

  const validate = () => {
    const errs = {};

    if (!form.shortName?.trim()) errs.shortName = 'Short name is required.';
    if (!form.fullName?.trim()) errs.fullName = 'Full legal name is required.';

    if (form.contactPhone && !isValidPhone(form.contactPhone)) {
      errs.contactPhone = 'Enter a valid phone number.';
    }

    if (form.website) {
      const w = form.website.trim();
      const looksLikeDomainOrUrl = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+.*$/i.test(w);
      if (!looksLikeDomainOrUrl) errs.website = 'Enter a valid website (e.g. www.example.com).';
    }

    if (!form.signatoryName?.trim()) errs.signatoryName = 'Signatory name is required.';
    if (!form.signatoryTitle?.trim()) errs.signatoryTitle = 'Signatory title is required.';

    if (form.smtpUser && !isValidEmail(form.smtpUser)) {
      errs.smtpUser = 'Enter a valid Gmail address.';
    }
    // Require app password only if a sender email was provided (or vice versa)
    if (form.smtpUser && !form.smtpAppPassword && !existing?.hasSmtpPassword) {
      errs.smtpAppPassword = 'App password is required when a sender email is set.';
    }
    if (form.smtpAppPassword && !form.smtpUser) {
      errs.smtpUser = 'Sender email is required when an app password is set.';
    }

    if (form.letterheadMode === 'FULL_PAGE') {
      const top = Number(form.contentTopMarginIn);
      const bottom = Number(form.contentBottomMarginIn);
      if (Number.isNaN(top) || top < 0 || top > 5) {
        errs.contentTopMarginIn = 'Enter a value between 0 and 5 inches.';
      }
      if (Number.isNaN(bottom) || bottom < 0 || bottom > 5) {
        errs.contentBottomMarginIn = 'Enter a value between 0 and 5 inches.';
      }
    }

    if (!existing?.letterheadPath && !letterheadFile && form.letterheadMode === 'FULL_PAGE') {
      errs.letterheadFile = 'A full-page template requires an uploaded image.';
    }

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      setError('Please fix the highlighted fields before saving.');
      return;
    }

    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (k === 'smtpAppPassword' && !v) return; // empty = keep the current password
        if (k === 'hasSmtpPassword') return; // display-only flag, not a real field
        fd.append(k, v);
      });
      if (letterheadFile) fd.append('letterhead', letterheadFile);
      if (signatureFile) fd.append('signature', signatureFile);

      if (existing?._id) {
        await api.put(`/companies/${existing._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/companies', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save company');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal" onSubmit={handleSubmit} noValidate>
        <h2>{existing ? 'Edit Company' : 'Add Company'}</h2>
        {error && <div className="login-err">{error}</div>}

        <div className="row2">
          <div className="fg">
            <label>Short Name</label>
            <input
              value={form.shortName}
              onChange={(e) => set('shortName', e.target.value)}
              placeholder="Medigenix Health"
            />
            {fieldErrors.shortName && <small className="field-err">{fieldErrors.shortName}</small>}
          </div>
          <div className="fg">
            <label>Full Legal Name</label>
            <input
              value={form.fullName}
              onChange={(e) => set('fullName', e.target.value)}
              placeholder="Medigenix Health LLC."
            />
            {fieldErrors.fullName && <small className="field-err">{fieldErrors.fullName}</small>}
          </div>
        </div>

        <div className="row2">
          <div className="fg">
            <label>Accent Color</label>
            <input type="color" value={form.accentColor} onChange={(e) => set('accentColor', e.target.value)} />
          </div>
          <div className="fg">
            <label>Contact Phone</label>
            <input
              value={form.contactPhone}
              onChange={(e) => set('contactPhone', e.target.value)}
              placeholder="(757) 744-4522"
            />
            {fieldErrors.contactPhone && <small className="field-err">{fieldErrors.contactPhone}</small>}
          </div>
        </div>

        <div className="fg">
          <label>Website / Public Email</label>
          <input value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="www.medigenixhealth.com" />
          {fieldErrors.website && <small className="field-err">{fieldErrors.website}</small>}
        </div>

        <div className="fg">
          <label>Footer Address Line</label>
          <input
            value={form.addressLine}
            onChange={(e) => set('addressLine', e.target.value)}
            placeholder="1007 Orange St. 4th Floor, Wilmington, DE"
          />
        </div>

        <div className="row2">
          <div className="fg">
            <label>Signatory Name</label>
            <input
              value={form.signatoryName}
              onChange={(e) => set('signatoryName', e.target.value)}
              placeholder="Jithin John"
            />
            {fieldErrors.signatoryName && <small className="field-err">{fieldErrors.signatoryName}</small>}
          </div>
          <div className="fg">
            <label>Signatory Title</label>
            <input value={form.signatoryTitle} onChange={(e) => set('signatoryTitle', e.target.value)} />
            {fieldErrors.signatoryTitle && <small className="field-err">{fieldErrors.signatoryTitle}</small>}
          </div>
        </div>

        <div className="fg">
          <label>Signatory's Signature Image</label>
          <input type="file" accept="image/*" onChange={(e) => setSignatureFile(e.target.files[0])} />
          {existing?.signatureImagePath && !signatureFile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
              <img
                src={existing.signatureImagePath}
                alt="Current signature"
                style={{ height: 36, background: '#f4f5f9', borderRadius: 4, padding: '2px 6px' }}
              />
              <small style={{ color: '#888', fontSize: 11 }}>Current signature on file — upload a new one to replace it.</small>
            </div>
          )}
          {!existing?.signatureImagePath && !signatureFile && (
            <small style={{ color: '#888', fontSize: 11, display: 'block', marginTop: 4 }}>
              Optional — ideally a PNG with a transparent background. Since this signatory is fixed, this same
              signature is embedded automatically on every offer letter (and shown in the live preview) instead of
              leaving a blank line to sign.
            </small>
          )}
        </div>

        <div className="fg">
          <label>Contact Email (shown in preview + used on internship offer letters)</label>
          <input
            value={form.contactEmail}
            onChange={(e) => set('contactEmail', e.target.value)}
            placeholder="jithin.j@medigenixhealth.com"
          />
        </div>

        <div className="fg">
          <label>Letterhead / Logo Image</label>
          <input type="file" accept="image/*" onChange={(e) => setLetterheadFile(e.target.files[0])} />
          {existing?.letterheadPath && !letterheadFile && (
            <small style={{ color: '#888', fontSize: 11 }}>Current file on record — upload a new one to replace it.</small>
          )}
          {fieldErrors.letterheadFile && <small className="field-err">{fieldErrors.letterheadFile}</small>}
        </div>

        <div className="fg">
          <label>Letterhead type</label>
          <select value={form.letterheadMode} onChange={(e) => set('letterheadMode', e.target.value)}>
            <option value="LOGO">Small logo (placed at top of page)</option>
            <option value="FULL_PAGE">Full-page template (branding baked into the image itself)</option>
          </select>
        </div>

        {form.letterheadMode === 'FULL_PAGE' && (
          <>
            <div className="row2">
              <div className="fg">
                <label>Page size</label>
                <select value={form.pageSize} onChange={(e) => set('pageSize', e.target.value)}>
                  <option value="LETTER">Letter (8.5 × 11 in)</option>
                  <option value="A4">A4 (8.27 × 11.69 in)</option>
                </select>
              </div>
            </div>
            <div className="row2">
              <div className="fg">
                <label>Top clear space (in)</label>
                <input
                  type="number"
                  step="0.1"
                  value={form.contentTopMarginIn}
                  onChange={(e) => set('contentTopMarginIn', e.target.value)}
                />
                {fieldErrors.contentTopMarginIn && <small className="field-err">{fieldErrors.contentTopMarginIn}</small>}
              </div>
              <div className="fg">
                <label>Bottom clear space (in)</label>
                <input
                  type="number"
                  step="0.1"
                  value={form.contentBottomMarginIn}
                  onChange={(e) => set('contentBottomMarginIn', e.target.value)}
                />
                {fieldErrors.contentBottomMarginIn && <small className="field-err">{fieldErrors.contentBottomMarginIn}</small>}
              </div>
            </div>
            <small style={{ color: '#888', fontSize: 11, display: 'block', marginTop: -6, marginBottom: 12 }}>
              These reserve blank space at the top/bottom of the page so the letter text doesn't overlap your template's artwork. Generate a test letter and adjust if needed.
            </small>
          </>
        )}

        <div className="sec-label">Unpaid Internship Defaults (optional)</div>
        <div className="row2">
          <div className="fg">
            <label>EIN Number</label>
            <input value={form.einNumber} onChange={(e) => set('einNumber', e.target.value)} placeholder="37-2181473" />
          </div>
          <div className="fg">
            <label>E-Verify Number</label>
            <input value={form.everifyNumber} onChange={(e) => set('everifyNumber', e.target.value)} placeholder="2732535" />
          </div>
        </div>
        <small style={{ color: '#888', fontSize: 11, display: 'block', marginTop: -6, marginBottom: 12 }}>
          Only used in the text of UNPAID internship offer letters for this company. Leave blank if this company
          never issues unpaid internships.
        </small>

        <div className="sec-label">Sender Email (used when this company sends offers)</div>
        <div className="fg">
          <label>Gmail Address</label>
          <input value={form.smtpUser} onChange={(e) => set('smtpUser', e.target.value)} placeholder="hr@gmail.com" />
          {fieldErrors.smtpUser && <small className="field-err">{fieldErrors.smtpUser}</small>}
        </div>
        <div className="fg">
          <label>Gmail App Password</label>
          <input
            type="password"
            value={form.smtpAppPassword}
            onChange={(e) => set('smtpAppPassword', e.target.value)}
            placeholder={existing?.hasSmtpPassword ? 'Saved — leave blank to keep it' : 'xxxx xxxx xxxx xxxx'}
          />
          {fieldErrors.smtpAppPassword && <small className="field-err">{fieldErrors.smtpAppPassword}</small>}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={busy}>{busy ? 'Saving…' : 'Save Company'}</button>
        </div>
      </form>
    </div>
  );
}
